import { Box, Button, Divider, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { RichTextEditor } from "./RichTextEditor";
import { useState } from "react";
import { useSettingsContext } from "../SettingsProvider";
import { v4 as uuid } from "uuid";
import { useForm } from "react-hook-form";
import { EmptyContent } from "@imminently/react/ui/core/empty-content";
import { AddIcon } from "@/assets/icons";
import { scrollable } from "@/components/scrollable";
import { Icon } from "@/components/Icon";
import { createAppResourceService } from "@/global";
import { useNotify } from "@imminently/react/store/notifications";

const metaService = createAppResourceService("meta");

type FAQ = { id: string, question: string, answer: string };

const FAQForm = ({ faq, onSubmit, onDelete }: { faq: FAQ, onSubmit: (data: any) => void, onDelete: (id: string) => void }) => {
  const [isNew] = useState(() => faq.question.length === 0);
  const methods = useForm<FAQ>({
    defaultValues: faq,
  });
  const { register, handleSubmit, setValue, watch } = methods;
  const question = watch("question");
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        <Typography variant="h4">{isNew ? "Add FAQ" : "Edit FAQ"}</Typography>
        <Stack gap={1}>
          <Typography variant="subtitle1">Question</Typography>
          <TextField autoFocus {...register("question")} fullWidth />
        </Stack>
        <Stack gap={1}>
          <Typography variant="subtitle1">Answer</Typography>
          <RichTextEditor initValue={faq.answer} onChange={(v) => setValue("answer", v)} />
        </Stack>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {!isNew && <Button color="error" variant="outlined" startIcon={<Icon icon="delete" />} onClick={() => onDelete(faq.id)}>Delete</Button>}
          <Button type="submit" startIcon={<Icon icon="save" />} disabled={question.length === 0}>Save</Button>
        </Box>
      </Stack>
    </form>
  );
};

export const FAQsPage = () => {
  // const { settings, setSettings } = useSettingsContext();
  const { data } = metaService.useGetOne("faqs", {}); // Hack to get `GET /meta/faqs` route
  const faqs = ((data as any)?.faqs ?? []) as Array<FAQ>;
  const { mutateAsync: updateFAQs, ...setQuery } = metaService.useUpdate();
  const [selectedFaq, setSelectedFaq] = useState<FAQ | null>(null);
  const [search, setSearch] = useState("");

  const notify = useNotify();
  const setFAQs = async (newData: any) => {
    if (!data) return;

    try {
      const res = await updateFAQs({ id: "faqs", payload: newData }); // Hack to get `PATCH /meta/faqs`
      notify.success("FAQs updated successfully!");
      console.log("Updated FAQs", res);
    } catch (err) {
      console.error("Failed to update FAQs:", err);
      notify.error("Failed to update FAQs");
    }
  };

  const createEmptyFaq = () => ({ id: uuid(), question: "", answer: "" });
  const onAddFaq = () => {
    // note we don't actually add it to the list until it's saved and has a question
    const newFaq = createEmptyFaq();
    setSelectedFaq(newFaq);
  };

  const onRemoveFaq = (id: string) => {
    const next = faqs.filter((faq) => faq.id !== id);
    setFAQs({ faqs: next });
    if (selectedFaq?.id === id) {
      setSelectedFaq(null);
    }
  };

  const onSubmitFaq = (faqData: FAQ) => {
    // console.log("submit", faqData);
    const exists = faqs.find((f) => f.id === faqData.id);
    const next = exists ? faqs.map((f) => (f.id === faqData.id ? faqData : f)) : [...faqs, faqData];
    setFAQs({ faqs: next });
    setSelectedFaq(null);
  };

  const searchProps = {
    htmlInput: {
      style: { paddingLeft: 0 },
    },
    input: {
      startAdornment: (
        <InputAdornment position="start">
          <Icon icon="search" />
        </InputAdornment>
      ),
    },
  };

  const filteredFaqs = faqs.filter((faq) => faq.question.toLowerCase().includes(search.toLowerCase()));

  return (
    <Stack flexDirection="row" width="100%" height="100%" minHeight={0}>
      <Box sx={{ borderRight: 1, borderColor: "divider", p: 2 }}>
        <Typography variant="h4">FAQs</Typography>
        <Stack sx={{ gap: 2, pt: 2, width: 250 }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddFaq}>Add</Button>
          <Divider />
          <TextField placeholder="Search FAQs" slotProps={searchProps} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Stack className={scrollable} sx={{ gap: 1, height: "100%", overflowX: "hidden" }}>
            {filteredFaqs.map((faq) => (
              <Box
                key={faq.id}
                onClick={() => setSelectedFaq(faq)}
                sx={{
                  cursor: "pointer",
                  padding: 1,
                  borderRadius: 1,
                  backgroundColor: selectedFaq?.id === faq.id ? "#e5e5e5" : "none",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                style={{
                  textAlign: "left",
                }}
              >
                {faq.question || "Untitled FAQ"}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, pt: 2, pl: 2 }}>
        {selectedFaq ? (
          <FAQForm key={selectedFaq.id} faq={selectedFaq} onSubmit={onSubmitFaq} onDelete={onRemoveFaq} />
        ) : (
          <EmptyContent image={null}>Select an FAQ to view or edit</EmptyContent>
        )}
      </Box>
    </Stack>
  );
};